import type { JSX } from "preact";
import { useState } from "preact/hooks";
import { clearSelection, createFolder, folderLabel, moveMessages } from "../actions";
import { mailboxes, movePicker } from "../state";
import { t } from "../i18n";
import { Icon } from "./icons";
import { Dialog } from "./kit";

/** "Move to…" folder picker with type-to-filter and inline folder creation. */
export function MovePickerDialog(): JSX.Element | null {
  const req = movePicker.value;
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  if (!req) return null;

  const close = (): void => {
    movePicker.value = null;
    setQ("");
    setActive(0);
  };
  const needle = q.trim().toLowerCase();
  const options = mailboxes.value
    .filter((m) => m.path !== req.mailbox && m.specialUse !== "drafts" && m.specialUse !== "sent")
    .map((m) => ({ path: m.path, label: m.specialUse ? folderLabel(m.path) : m.path.replace(new RegExp(`\\${m.delimiter}`, "g"), " / "), use: m.specialUse }))
    .filter((o) => !needle || o.label.toLowerCase().includes(needle));
  const canCreate = needle.length > 0 && !mailboxes.value.some((m) => m.name.toLowerCase() === needle);

  async function choose(path: string): Promise<void> {
    const refs = req!.uids.map((uid) => ({ uid, mailbox: req!.mailbox }));
    close();
    clearSelection();
    await moveMessages(refs, path);
  }

  async function createAndMove(): Promise<void> {
    const name = q.trim();
    await createFolder(name);
    const box = mailboxes.value.find((m) => m.name === name || m.path === name);
    if (box) await choose(box.path);
  }

  return (
    <Dialog onClose={close} label={t("move.title")} class="max-w-sm" align="top">
      <div class="flex items-center gap-2 border-b border-line px-4">
        <Icon name="folderMove" size={17} class="text-fg-3" />
        <input
          autofocus
          class="h-12 flex-1 bg-transparent text-[14px] text-fg outline-none placeholder:text-fg-4"
          placeholder={t("move.placeholder", { n: req.uids.length })}
          value={q}
          onInput={(e) => {
            setQ((e.currentTarget as HTMLInputElement).value);
            setActive(0);
          }}
          onKeyDown={(e) => {
            const total = options.length + (canCreate ? 1 : 0);
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              setActive((a) => (a + (e.key === "ArrowDown" ? 1 : -1) + total) % Math.max(total, 1));
            }
            if (e.key === "Enter") {
              e.preventDefault();
              const o = options[active];
              if (o) void choose(o.path);
              else if (canCreate) void createAndMove();
            }
          }}
        />
      </div>
      <ul class="max-h-[50vh] overflow-y-auto p-1" role="listbox">
        {options.map((o, i) => (
          <li key={o.path}>
            <button
              class="menu-item"
              data-active={i === active ? "true" : undefined}
              onMouseEnter={() => setActive(i)}
              onClick={() => void choose(o.path)}
            >
              <Icon name={o.use === "inbox" ? "inbox" : o.use === "archive" ? "archive" : o.use === "trash" ? "trash" : o.use === "junk" ? "spam" : "folder"} size={16} />
              <span class="truncate">{o.label}</span>
            </button>
          </li>
        ))}
        {canCreate && (
          <li>
            <button
              class="menu-item"
              data-active={active === options.length ? "true" : undefined}
              onMouseEnter={() => setActive(options.length)}
              onClick={() => void createAndMove()}
            >
              <Icon name="folderPlus" size={16} />
              <span class="truncate">{t("move.create", { name: q.trim() })}</span>
            </button>
          </li>
        )}
        {!options.length && !canCreate && <li class="px-3 py-4 text-center text-[13px] text-fg-3">{t("move.none")}</li>}
      </ul>
    </Dialog>
  );
}
